<script>
import agreementApi from '@/api/agreement';
import externalContactApi from '@/api/externalContact';
import Access from '@/utils/access';
import customSettings from '@/model/userCustomSettings';

import CommonSection from '@/components/CommonSection.vue';
import taskListModel from '@/model/taskList';
import AgreementList from '@/components/agreement/AgreementList.vue';
import HelpPopup from '@/components/base/HelpPopup.vue';
import {markVisited} from '@/utils/newPage';

export default {
  name: 'AgreementListPage',
  components: { AgreementList, CommonSection, HelpPopup },
  data() {
    return {
      agreements: [],
      externalContacts: [],
    };
  },
  computed: {
    currUser() { return this.$store.state.auth.user; },
    access() { return new Access(this.project, this.currUser); },
    project() { return this.$store.state.project.project; },
    stages() { return this.$store.getters['stage/itemsHydrated']; },
    tasks() { return this.$store.getters['taskList/byStages']; },
  },
  watch: {
    project: {
      immediate: true,
      handler(val) {
        if (!this.project) return;
        const access = new Access(val, this.$store.state.auth.user);

        const hasAccess = access.isProjectAdmin() || access.isProjectAuthor();
        if (!hasAccess) {
          this.$store.commit('error/add', { status: 403 });
        }
      },
    },
  },
  created() {
    const { projectId } = this.$route.params;
    this.$store.commit('taskList/set', []);

    taskListModel.fetch({ 'stage.project': projectId });
    agreementApi.fetchList({ 'comment.task.stage.project': projectId }, true)
      .then(r => { this.agreements = r.items; });
    externalContactApi.fetchList({ 'project': projectId }, true)
      .then(r => { this.externalContacts = r.items; });

    markVisited('agreements');
  },
  methods: {
    agreementDeleted(agreement) {
      this.agreements = this.agreements.filter(a => a.id !== agreement.id);
    },
  },
};
</script>

<template lang="pug">
CommonSection(title="Согласования" :back="{ title: project && project.name, name: 'project-stages', params: { projectId: $route.params.projectId } }")
  template(#title)
    HelpPopup(guide="https://teroom.ru/guides/74")
      template(#default)
        h2 Страница «Согласования»
        p Единый центр контроля внешних согласований с заказчиками и контрагентами. Прозрачный учет времени, затраченного заказчиком на согласования.
        p Система автоматически фиксирует фактические сроки ответа, среднее время ожидания и просроченные события, формируя объективную доказательную базу.
        p Сформированный в PDF‑формате отчет по всем срокам согласований становится весомым аргументом при обсуждении задержек и позволяет аргументированно управлять сроками проекта.
      template(#details)
        h3 Основная информация
        p На данной странице отображаются все Согласования с заказчиком или любым другим внешним пользователем — по разделам и задачам. Согласования разделяются по статусам: «<b>Ожидает согласования</b>», «<b>Согласовано</b>» и «<b>Отправлено на доработку</b>».
        p В статистике отображаются данные по среднему времени согласований, общему времени, которое потребовалось заказчику (или внешнему пользователю) на согласования, текущему времени по согласованиям, оставшимся без внимания, а также количеству согласованных, отклоненных (отправленных на доработку) и ожидающих событий.
        p Статистику можно отфильтровать, например, по имени внешнего пользователя или в рамках одного раздела, а также по одному или нескольким статусам.
        p Ссылку на любое согласование можно скопировать в любое время и при необходимости повторно отправить внешнему адресату.

        h3 Важно!
        ul
          li Все события по согласованию создаются из комментария в рамках задачи текущего проекта.
          li Согласование можно отправить любому пользователю, даже не зарегистрированному в TeRoom.
          li Согласование можно удалить или отредактировать только до того момента, пока на него не ответил адресат.
          li Ссылка на согласование бессрочна, поэтому может отправляться повторно.
          li Создать событие по согласованию из комментария задачи может только автор проекта, админ проекта или автор задачи.
          li Общую страницу со всеми событиями согласований видят только автор проекта и админ.
          li Все события и статистику по времени и количеству согласований с описанием и прикреплёнными файлами можно экспортировать в PDF-файл.
          li Администрация TeRoom рекомендует использовать данный функционал и отправлять ссылки на согласования по электронной почте или иными средствами коммуникаций, закрепленными в тексте вашего договора с заказчиком. Главное — чтобы в случае необходимости вы могли быстро сопоставить факт (дата, время) создания события по согласованию в TeRoom и факт отправки ссылки заказчику.
          li Отменить или как-либо изменить состоявшееся событие («Согласовано» или «Отправлено на доработку») невозможно.
          li Система не учитывает выходные дни — субботу и воскресенье по московскому часовому поясу.

  AgreementList(
    v-if="project"
    :stages="stages"
    :tasks="tasks"
    :agreements="agreements"
    :externalContacts="externalContacts"
    @agreementDeleted="agreementDeleted"
  )
</template>

<style scoped lang="scss">
</style>
